import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserPlus, KeyRound, ShieldOff, RefreshCw, Copy, Mail, MessageCircle, Power, Loader2, Ban } from "lucide-react";

const ROLE_LABEL = { cliente: "Cliente", admin: "Administrador" };
const TYPE_LABEL = { permanent: "Permanente", temporary: "Temporal", one_shot: "Un solo uso" };
const STATUS_LABEL = { activo: "Activo", revocado: "Revocado" };

export default function UsersAndAccessPanel() {
  const [requireCode, setRequireCode] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openUser, setOpenUser] = useState(null); // usuario abierto en modal edit / autorizaciones
  const [openCreate, setOpenCreate] = useState(false);
  const [showCode, setShowCode] = useState(null); // {code_plain, stands, user, authId}

  const load = async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([
        api.get("/admin/settings/require-access-code"),
        api.get("/admin/users"),
      ]);
      setRequireCode(!!t.data?.require_access_code);
      setUsers(u.data || []);
    } catch (e) {
      console.error("load users", e);
      toast.error("No se pudo cargar la lista de usuarios");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleRequire = async (v) => {
    try {
      await api.put("/admin/settings/require-access-code", { enabled: v });
      setRequireCode(v);
      toast.success(v ? "Ahora se exige código de acceso a los clientes." : "Modo transición activo (código opcional).");
    } catch { toast.error("No se pudo cambiar el toggle"); }
  };

  const toggleUser = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/toggle`);
      toast.success(data.status === "activo" ? "Usuario activado" : "Usuario desactivado (accesos revocados)");
      load();
    } catch { toast.error("No se pudo cambiar el estado del usuario"); }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-950 border-zinc-800 rounded-sm p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Configuración global</div>
            <div className="text-sm text-white mt-1">Exigir código de acceso a los clientes</div>
            <div className="text-[11px] text-zinc-500 max-w-lg mt-1">
              Activá esto cuando ya hayas generado y enviado los códigos a todos los expositores. Con el switch apagado, el ingreso funciona solo con número de stand (modo transición).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${requireCode ? "text-emerald-300" : "text-zinc-500"}`}>{requireCode ? "EXIGIR CÓDIGO" : "TRANSICIÓN"}</span>
            <Switch data-testid="require-code-toggle" checked={requireCode} onCheckedChange={toggleRequire} />
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Usuarios y Accesos</h2>
          <p className="text-xs text-zinc-500">Clientes autorizados a autogestionar sus stands.</p>
        </div>
        <Button data-testid="create-user-btn" onClick={() => setOpenCreate(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
          <UserPlus className="w-4 h-4 mr-2" /> Crear usuario
        </Button>
      </div>

      <Card className="bg-zinc-950 border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Nombre</th><th className="text-left p-3">Empresa</th><th className="text-left p-3">Contacto</th><th className="text-left p-3">Rol</th><th className="text-left p-3">Estado</th><th className="text-left p-3">Últ. acceso</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center p-6 text-zinc-500 text-xs"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></td></tr>}
              {!loading && users.length === 0 && <tr><td colSpan={7} className="text-center p-8 text-zinc-500 text-xs">No hay usuarios cargados. Creá el primero.</td></tr>}
              {!loading && users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                  <td className="p-3">{u.name} {u.surname || ""}</td>
                  <td className="p-3">{u.company || "—"}</td>
                  <td className="p-3 text-xs text-zinc-400">{u.email}<br /><span className="text-zinc-500">{u.whatsapp}</span></td>
                  <td className="p-3 text-xs">{ROLE_LABEL[u.role] || u.role}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm border ${u.status === "activo" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-zinc-500 border-zinc-700 bg-zinc-900"}`}>
                      {u.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-zinc-500 font-mono">{u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setOpenUser(u)} data-testid={`open-user-${u.id}`} className="h-8 text-xs">
                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Accesos
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleUser(u)} className="h-8 text-xs" title={u.status === "activo" ? "Desactivar" : "Activar"}>
                        {u.status === "activo" ? <Power className="w-3.5 h-3.5 text-red-400" /> : <Power className="w-3.5 h-3.5 text-emerald-400" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {openCreate && <CreateUserDialog onClose={(created) => { setOpenCreate(false); if (created) load(); }} />}
      {openUser && <UserAccessDialog user={openUser} onClose={() => { setOpenUser(null); load(); }} onCodeGenerated={setShowCode} />}
      {showCode && <ShowCodeDialog data={showCode} onClose={() => setShowCode(null)} />}
    </div>
  );
}

function CreateUserDialog({ onClose }) {
  const [form, setForm] = useState({ name: "", surname: "", company: "", position: "", email: "", whatsapp: "", role: "cliente", observaciones: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Usuario creado");
      onClose(true);
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "No se pudo crear el usuario");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent data-testid="create-user-dialog" className="bg-zinc-950 border-zinc-800 text-white max-w-lg">
        <DialogHeader><DialogTitle>Crear usuario</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-xs">
          {[["name", "Nombre *"], ["surname", "Apellido"], ["company", "Empresa"], ["position", "Cargo"], ["email", "Email"], ["whatsapp", "WhatsApp"]].map(([k, l]) => (
            <div key={k}>
              <div className="text-zinc-500 uppercase text-[10px] mb-1">{l}</div>
              <Input data-testid={`create-user-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-zinc-900 border-zinc-800 h-9 text-xs" />
            </div>
          ))}
          <div className="col-span-2">
            <div className="text-zinc-500 uppercase text-[10px] mb-1">Rol</div>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="create-user-role" className="bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-white"><SelectItem value="cliente">Cliente</SelectItem><SelectItem value="admin">Administrador</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <div className="text-zinc-500 uppercase text-[10px] mb-1">Observaciones</div>
            <Textarea data-testid="create-user-obs" rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} className="bg-zinc-900 border-zinc-800 text-xs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>Cancelar</Button>
          <Button data-testid="save-user-btn" onClick={save} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserAccessDialog({ user, onClose, onCodeGenerated }) {
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [standsInput, setStandsInput] = useState("");
  const [codeType, setCodeType] = useState("permanent");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/authorizations`);
      setAuths(data || []);
    } catch { toast.error("No se pudieron cargar los accesos"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user.id]);

  const generate = async () => {
    const stands = standsInput.split(/[,\s]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
    if (!stands.length) return toast.error("Ingresá al menos un stand (ej: T16 T17 o T16,T17)");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/authorizations`, { stand_codes: stands, code_type: codeType });
      onCodeGenerated({ code_plain: data.code_plain, stands, user, authId: data.id });
      setStandsInput("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el código");
    } finally { setSaving(false); }
  };

  const revoke = async (aid) => {
    if (!window.confirm("¿Revocar este acceso? El cliente no podrá volver a ingresar con este código.")) return;
    try { await api.post(`/admin/authorizations/${aid}/revoke`); toast.success("Acceso revocado"); load(); } catch { toast.error("Error al revocar"); }
  };
  const regen = async (aid) => {
    if (!window.confirm("¿Regenerar el código? Esto invalida el actual inmediatamente.")) return;
    try {
      const { data } = await api.post(`/admin/authorizations/${aid}/regenerate`);
      onCodeGenerated({ code_plain: data.code_plain, stands: data.stand_codes, user, authId: data.id });
      load();
    } catch { toast.error("Error al regenerar"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-access-dialog" className="bg-zinc-950 border-zinc-800 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle>{user.name} {user.surname || ""} · Accesos a stands</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-[#FF5A00]/30 bg-[#FF5A00]/5 rounded-sm p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A00]">Generar código</div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <div className="text-zinc-500 text-[10px] mb-1">Stands (separados por coma o espacio)</div>
                <Input data-testid="new-auth-stands" placeholder="Ej: T16, T17" value={standsInput} onChange={(e) => setStandsInput(e.target.value.toUpperCase())} className="bg-zinc-900 border-zinc-800 h-9 font-mono text-xs" />
              </div>
              <div>
                <div className="text-zinc-500 text-[10px] mb-1">Tipo</div>
                <Select value={codeType} onValueChange={setCodeType}>
                  <SelectTrigger data-testid="new-auth-type" className="bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                    <SelectItem value="permanent">Permanente</SelectItem>
                    <SelectItem value="temporary">Temporal</SelectItem>
                    <SelectItem value="one_shot">Un solo uso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="generate-code-btn" onClick={generate} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-9 text-xs w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-3.5 h-3.5 mr-1.5" /> Generar código</>}
            </Button>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Accesos existentes</div>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
              auths.length === 0 ? <div className="text-xs text-zinc-500 py-3">Sin accesos generados aún.</div> : (
                <div className="space-y-2">
                  {auths.map((a) => (
                    <div key={a.id} data-testid={`auth-row-${a.id}`} className="border border-zinc-800 rounded-sm p-3 flex items-center gap-3 text-xs">
                      <div className="flex-1">
                        <div className="font-mono text-white">{a.stand_codes.join(" + ")}</div>
                        <div className="text-zinc-500 text-[10px]">
                          {TYPE_LABEL[a.code_type]} · Últimos 4: <span className="font-mono">{a.code_last4}</span> · {a.created_at?.slice(0, 10)}
                        </div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm border ${a.status === "activo" ? "text-emerald-300 border-emerald-500/40" : "text-zinc-500 border-zinc-700"}`}>{STATUS_LABEL[a.status] || a.status}</span>
                      {a.status === "activo" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => regen(a.id)} className="h-8" title="Regenerar código"><RefreshCw className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => revoke(a.id)} className="h-8 text-red-400" title="Revocar"><Ban className="w-3.5 h-3.5" /></Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShowCodeDialog({ data, onClose }) {
  const [sending, setSending] = useState(false);
  const { code_plain, stands, user, authId } = data;
  const stand_list = stands.join(", ");
  const whatsappMsg = `Hola, ${user.name}.\nYa podés gestionar los servicios de tu stand para Argentina Mining Norte 2026.\nStand: ${stand_list}\nCódigo de acceso: ${code_plain}\nIngresá en:\nhttps://full.serviciosoficiales.com.ar/\nEl código es personal. No lo compartas con personas no autorizadas.`;
  const copyCode = () => { navigator.clipboard.writeText(code_plain); toast.success("Código copiado"); };
  const copyMsg = () => { navigator.clipboard.writeText(whatsappMsg); toast.success("Mensaje copiado (pegalo en WhatsApp)"); };
  const sendMail = async () => {
    if (!user.email) return toast.error("El usuario no tiene email cargado");
    setSending(true);
    try {
      await api.post(`/admin/authorizations/${authId}/send`, { code_plain, channel: "email" });
      toast.success(`Correo enviado a ${user.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="show-code-dialog" className="bg-zinc-950 border-[#FF5A00]/60 text-white max-w-md">
        <DialogHeader><DialogTitle>Código generado — mostrálo una sola vez</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-zinc-500">Stand(s): <span className="font-mono text-white">{stand_list}</span></div>
          <div className="border border-[#FF5A00] bg-[#FF5A00]/10 rounded-sm p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A00] mb-1">Código</div>
            <div data-testid="new-code-value" className="font-mono text-3xl font-bold tracking-widest text-white">{code_plain}</div>
          </div>
          <div className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm p-2">
            ⚠ Guardá o copiá este código ahora. Por seguridad no lo mostraremos de nuevo. Si el cliente lo pierde, tenés que regenerarlo.
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button data-testid="copy-code-btn" onClick={copyCode} variant="outline" className="border-zinc-700 h-9 text-xs"><Copy className="w-3.5 h-3.5 mr-1" /> Copiar código</Button>
            <Button data-testid="copy-msg-btn" onClick={copyMsg} variant="outline" className="border-zinc-700 h-9 text-xs"><MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp</Button>
            <Button data-testid="send-mail-btn" onClick={sendMail} disabled={sending} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-9 text-xs">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Email</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
